/* eslint-disable max-lines */
import {
  usePersonMatchProfiles,
  useTenantDetails,
} from '@hakimo-ui/hakimo/data-access';
import { PersonMatchProfile } from '@hakimo-ui/hakimo/types';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  useCanModifyPersonProfiles,
  useCanViewPersonProfiles,
  useDebounce,
} from '@hakimo-ui/hakimo/util';
import { Alert, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import AddEditProfileModal from './AddEditProfileModal';
import DeleteProfilesModal from './DeleteProfilesModal';
import { FilterPanel } from './filter-panel';
import { faceRecognitionFiltersAtom, faceRecognitionSearchAtom } from './state';
import TableHeader from './table/TableHeader';
import { DEFAULT_PERSON_PROFILE_FILTERS, PersonProfileFilters } from './types';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  columns,
  getPersonProfileSearchParams,
  getTableData,
  hasFiltersApplied,
} from './utils';

interface Props {
  locationId: number;
  tenantId: string;
}

export function FaceRecognition(props: Props) {
  const { locationId, tenantId } = props;
  const canModifyProfiles = useCanModifyPersonProfiles();
  const canViewProfiles = useCanViewPersonProfiles();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [tableData, setTableData] = useState<TableData>({ columns, rows: [] });
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProfile, setSelectedProfile] =
    useState<PersonMatchProfile | null>(null);
  const [filtersAtom] = useState(() => faceRecognitionFiltersAtom(locationId));
  const [searchAtom] = useState(() => faceRecognitionSearchAtom(locationId));
  const [filters, setFilters] = useAtom(filtersAtom);
  const [searchValue, setSearchValue] = useAtom(searchAtom);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, 400);

  const { data: tenantData } = useTenantDetails(tenantId);
  const showBadgeId = tenantData?.config?.showBadgeId ?? false;
  const safeFilters = filters || DEFAULT_PERSON_PROFILE_FILTERS;

  const { data, isLoading, isError, error } = usePersonMatchProfiles(
    [locationId],
    getPersonProfileSearchParams(
      safeFilters,
      page,
      DEFAULT_PAGE_SIZE,
      debouncedSearchValue,
      showBadgeId
    ),
    true,
    undefined
  );

  const fromIndex = (page - 1) * DEFAULT_PAGE_SIZE;
  const toIndex = Math.min(page * DEFAULT_PAGE_SIZE, total) - 1;

  const onClickProfile = useCallback(
    (profile: PersonMatchProfile) => {
      if (canModifyProfiles || canViewProfiles) {
        setSelectedProfile(profile);
      }
    },
    [canModifyProfiles, canViewProfiles]
  );

  const onChangeSelectedProfiles = (selected: string[]) => {
    setSelectedProfiles(selected);
  };

  const onDeleteProfiles = () => {
    setShowDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    setSelectedProfiles([]);
  };

  useEffect(() => {
    if (data) {
      const { items, total: totalItems } = data;
      setTableData(getTableData(items, onClickProfile, showBadgeId));
      setTotal(totalItems);
    }
  }, [data, showBadgeId, onClickProfile]);

  const onChangePage = (newPage: number) => {
    setPage(newPage);
    setSelectedProfiles([]);
  };

  const onClickAddProfile = () => {
    setShowAddModal(true);
  };

  const onCloseModal = () => {
    setShowAddModal(false);
    setSelectedProfile(null);
  };

  const onClickFilter = () => {
    setShowFilterPanel(true);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(DEFAULT_PAGE);
  };

  const onCloseFilterPanel = () => {
    setShowFilterPanel(false);
  };

  const onApplyFilters = (newFilters: PersonProfileFilters) => {
    setFilters(newFilters);
    setPage(DEFAULT_PAGE);
    setShowFilterPanel(false);
  };

  const tableHeader = (
    <TableHeader
      onClickAddProfile={onClickAddProfile}
      onClickFilter={onClickFilter}
      isLoading={isLoading}
      hasFiltersApplied={hasFiltersApplied(safeFilters)}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      selectedProfilesCount={selectedProfiles.length}
      onDeleteProfiles={onDeleteProfiles}
      canModifyProfiles={canModifyProfiles}
    />
  );

  const tableFooter = (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 border-t p-4">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={DEFAULT_PAGE_SIZE}
        total={total}
        disabled={isLoading}
        onChangePage={onChangePage}
      />
    </div>
  );

  return (
    <div className="relative px-8">
      {isError && (
        <div className="mb-2">
          <Alert type="error">
            {error?.message || 'Failed to load face recognition profiles'}
          </Alert>
        </div>
      )}

      {isLoading && (
        <>
          <div className="dark:bg-dark-bg/70 absolute inset-0 z-50 bg-white/70"></div>
          <div className="absolute inset-0 z-50 flex items-center justify-center">
            <HakimoSpinner />
          </div>
        </>
      )}

      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 -mx-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden sm:-mx-6 md:mx-0 md:rounded-lg">
        <Table
          data={tableData}
          header={tableHeader}
          footer={tableFooter}
          scrollResetKey={page}
          rowSelection={{
            selected: selectedProfiles,
            onChangeSelected: onChangeSelectedProfiles,
          }}
        />
      </div>

      {showAddModal && (
        <AddEditProfileModal
          isEditing={false}
          onClose={onCloseModal}
          locationId={locationId}
          tenantId={tenantId}
          showBadgeId={showBadgeId}
        />
      )}

      {selectedProfile && (
        <AddEditProfileModal
          isEditing={true}
          onClose={onCloseModal}
          profile={selectedProfile}
          showBadgeId={showBadgeId}
          readOnly={!canModifyProfiles}
        />
      )}

      {showDeleteModal && (
        <DeleteProfilesModal
          profileIds={selectedProfiles}
          profileCount={selectedProfiles.length}
          locationId={locationId}
          onClose={onCloseDeleteModal}
          onSuccess={onDeleteSuccess}
        />
      )}

      <FilterPanel
        open={showFilterPanel}
        filters={safeFilters}
        onClose={onCloseFilterPanel}
        onApplyFilters={onApplyFilters}
        showBadgeId={showBadgeId}
      />
    </div>
  );
}

export default FaceRecognition;
